import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import {
  EmptyRewardCard,
  isPlayerPending,
} from "@/components/organisms/SBCs/RewardPlayerCard";
import { TradeableChip } from "@/components/organisms/SBCs/TradeableChip";
import { ViewRewardButton } from "@/components/organisms/SBCs/ViewRewardButton";
import PlayerCard from "@/design-system/PlayerCard/PlayerCard";
import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { extractRewardUrl } from "@/functions/extractRewardUrl";
import { cn } from "@/lib/utils";
import { tierSelector } from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

type Props = {
  reward: SBCRewardType;
  setName?: string;
  setDescription?: string;
  onlyShowImage?: boolean;
  /** The mobile row: same parts, sized for a phone and aligned left. */
  compact?: boolean;
  onClick?: () => void;
};

export const SetRewardHeader = ({
  reward,
  setName,
  setDescription,
  onlyShowImage = false,
  compact = false,
  onClick,
}: Props) => {
  const {
    type,
    metaRating,
    untradeable,
    name,
    description: rewardDescription,
  } = reward;

  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const { t } = useTranslation("sbcs");

  const rewardImage = extractRewardUrl(reward);
  const isEventToken2 = reward.type === "event_token_2";

  const rewardTitle = type === "player" ? setName : name;
  const description =
    type === "player" ||
    type === "playerpick" ||
    type === "cosmetic-evo" ||
    type === "event_token_2"
      ? setDescription
      : rewardDescription;

  /* A player reward is drawn as the card itself; its rating rides in the text
     column beside it rather than under it, which costs a row of height. */
  const playerCard = type === "player" && reward.player;

  const tier = useSelector(
    tierSelector(
      metaRating?.metarating || 0,
      metaRating?.playerRoleId,
      /* The chip's tier wants the new table: ring plus contrasting letter. */
      true,
    ),
  );

  return (
    <div
      className={cn(
        "flex items-center",
        compact ? "justify-start gap-x-3" : "justify-center",
        /* The artwork bleeds to its own edges; a card stops at a hard border
           and needs the space spelled out. */
        playerCard && "gap-x-5",
      )}
    >
      <div
        className={cn(
          "flex flex-col justify-center items-center gap-y-1.5 p-0 m-0",
          isEventToken2
            ? "min-w-14 min-h-14"
            : compact
              ? "min-w-20 min-h-20"
              : "min-w-28 min-h-28",
        )}
      >
        {playerCard && isPlayerPending(reward.player) ? (
          <EmptyRewardCard scale={compact ? 0.55 : 0.75} />
        ) : playerCard ? (
          <>
            <PlayerCard
              player={reward.player}
              position={reward.player.preferredPosition}
              scale={compact ? 0.55 : 0.75}
              hidePrice
            />
            {onClick && <ViewRewardButton onClick={onClick} />}
          </>
        ) : (
          <img
            src={rewardImage}
            alt={rewardTitle || t("alt.sbcLogo")}
            className={cn(
              "transition-all duration-300",
              isEventToken2
                ? "max-h-18 touch-device:max-h-[75px] m-2"
                : compact
                  ? "max-h-24"
                  : "max-h-36 touch-device:max-h-[150px]",
              reward.type === "pack" && "p-3",
            )}
            onClick={onClick}
          />
        )}
      </div>

      {!onlyShowImage && (
        <div className={cn("flex flex-row my-auto")}>
          <div
            className={cn(
              "flex flex-col gap-y-2 text-white",
              isMobile && "p-0 m-0 gap-y-1",
            )}
          >
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-card-gold">
                {t("setHeader.mainReward")}
              </span>
              <TradeableChip rewardType={type} untradeable={untradeable} />
            </div>
            <p
              className={cn(
                "font-semibold leading-tight text-foreground",
                compact ? "text-base" : "text-lg desktop:text-2xl",
              )}
            >
              {rewardTitle}
            </p>

            {playerCard && metaRating && tier && (
              <div className="w-fit">
                <MetaRatingChip
                  metaRating={metaRating.metarating}
                  tier={tier}
                />
              </div>
            )}

            <p
              className={cn(
                "text-foreground-muted large:max-w-[300px] mt-1 line-clamp-3",
                compact ? "text-tiny" : "text-xs",
                isMobile && "max-w-full",
              )}
            >
              {description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
